// src/components/molecules/InteractionTooltip.js

import React, { useState, useEffect } from "react";
import InteractionOverlay from "../atoms/InteractionOverlay";
import PropTypes from "prop-types";

/**
 * InteractionTooltip Component
 *
 * Displays the InteractionOverlay when the video's current time reaches the interaction's timestamp.
 * Automatically hides the overlay after a specified duration or when the user closes it.
 *
 * Props:
 * - interaction: Object containing interaction details (title, description, media, CTA, etc.).
 * - currentTime: Current playback time of the video in seconds.
 * - onClose: Function to call when the overlay is closed.
 * - displayDuration: Duration in milliseconds to display the overlay before auto-hiding (default: 10000ms).
 */
const InteractionTooltip = ({
  interaction,
  currentTime,
  onClose,
  displayDuration = 10000,
}) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Determine if the current time is within 1 second of the interaction's timestamp
    if (Math.abs(currentTime - interaction.timestamp) < 1) {
      setVisible(true);

      // Set a timer to auto-hide the overlay after displayDuration
      const timer = setTimeout(() => {
        setVisible(false);
        onClose();
      }, displayDuration);

      // Cleanup the timer when the component unmounts or when visible changes
      return () => clearTimeout(timer);
    }
  }, [currentTime, interaction.timestamp, onClose, displayDuration]);

  const handleClose = () => {
    setVisible(false);
    onClose();
  };

  if (!visible) return null;

  return <InteractionOverlay interaction={interaction} onClose={handleClose} />;
};

InteractionTooltip.propTypes = {
  interaction: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    mediaURL: PropTypes.string,
    mediaType: PropTypes.oneOf(["image", "video"]),
    callToAction: PropTypes.string,
    callToActionURL: PropTypes.string,
    timestamp: PropTypes.number.isRequired,
  }).isRequired,
  currentTime: PropTypes.number.isRequired,
  onClose: PropTypes.func,
  displayDuration: PropTypes.number,
};

InteractionTooltip.defaultProps = {
  onClose: () => {},
  displayDuration: 10000,
};

export default InteractionTooltip;
